/**
 * cpFromHp.ts — the CP a Pokémon must have, worked out from its max HP and
 * its IVs (2026-09-20 owner ask: "if CP cannot be read, display the expected
 * CP on the HUD and set it on the record; I tested three and it was spot on").
 *
 * WHY IT WORKS. Max HP is floor((baseSta + ivSta) x CPM(level)), so a known
 * HP names the level (or a couple of adjacent half-levels), and the level
 * with the IVs names the CP exactly. The three the owner tested were low
 * level, where every half-level has its own HP.
 *
 * WHERE IT IS NOT EXACT (measured 2026-09-20 over 150 species x 5 IV sets):
 * below level 20 an HP is shared by two half-levels 0.5% of the time; from
 * 20.5 to 35 it is 33%; above 35 nearly always, because the CPM steps shrink
 * to less than one HP point. The candidates then differ by about 1% of CP.
 * A wild catch, hatch or raid catch is always a WHOLE level (half-levels
 * only come from power-ups), so when exactly one candidate sits on a whole
 * level that is the pick; otherwise the median candidate. Either way the
 * result is flagged `exact: false`, the HUD prints it with a tilde, and the
 * record marks the CP as calculated (cpDerived) so a later scan that reads
 * the real CP corrects it rather than minting a twin.
 *
 * Pure. No DOM, no I/O.
 */
import { computeCp, computeHp } from "./ivSolver";
import { baseStatsCandidatesForSpecies, baseStatsForRecord } from "./myPokemonDerived";

export interface CpFromHp {
  /** The CP to show or store. */
  cp: number;
  /** The level that CP sits on. */
  level: number;
  /** True when this max HP fits exactly one CP for these IVs. */
  exact: boolean;
  /** Every (level, cp) the HP fits, level order. One entry when exact. */
  candidates: Array<{ level: number; cp: number }>;
}

export interface CpFromHpInput {
  speciesId: number | null;
  /** The record's form: an alternate form has its own curve (2026-09-22). */
  formId?: string | null;
  gender?: "male" | "female" | null;
  ivAtk: number | null;
  ivDef: number | null;
  ivHp: number | null;
  hpMax: number | null;
}

/**
 * The CP these IVs give at this max HP, or null when the species, the IVs
 * or the HP are unknown, or no level produces the HP (a misread).
 */
export function cpFromHp(input: CpFromHpInput): CpFromHp | null {
  if (input.hpMax == null || input.ivAtk == null || input.ivDef == null || input.ivHp == null) return null;
  const base = baseStatsForRecord(input);
  if (!base) return null;
  const iv = { atk: input.ivAtk, def: input.ivDef, sta: input.ivHp };
  const candidates: Array<{ level: number; cp: number }> = [];
  for (let half = 2; half <= 102; half++) {
    const level = half / 2;
    if (computeHp(base, iv.sta, level) !== input.hpMax) continue;
    candidates.push({ level, cp: computeCp(base, iv, level) });
  }
  if (candidates.length === 0) return null;
  const distinctCps = new Set(candidates.map((c) => c.cp));
  if (distinctCps.size === 1) {
    // Several half-levels can share both the HP and the CP at the bottom of
    // the ladder; the highest keeps the notch, as manualCpLadder does.
    const top = candidates[candidates.length - 1];
    return { cp: top.cp, level: top.level, exact: true, candidates };
  }
  const whole = candidates.filter((c) => Number.isInteger(c.level));
  const pick = whole.length === 1 ? whole[0] : candidates[Math.floor((candidates.length - 1) / 2)];
  return { cp: pick.cp, level: pick.level, exact: false, candidates };
}

/**
 * Whether a CP read sits on ANY level of these IVs' ladder, for the scan
 * gate when the max HP is not on screen (the appraisal overlay hides it): a
 * banner read truncated by the sprite ("CP5397" as 539) almost never lands
 * on one of the ~100 legal CPs. Null when it cannot be judged.
 */
export function cpOnIvLadder(
  input: Pick<CpFromHpInput, "speciesId" | "formId" | "gender" | "ivAtk" | "ivDef" | "ivHp">,
  cp: number,
): boolean | null {
  if (input.ivAtk == null || input.ivDef == null || input.ivHp == null) return null;
  const base = baseStatsForRecord(input);
  if (!base) return null;
  const iv = { atk: input.ivAtk, def: input.ivDef, sta: input.ivHp };
  return cpOnLadderOf(base, iv, cp);
}

/**
 * The same gate when the FORM is not yet known (a live-scan frame names the
 * species, never "Origin Forme"): true when the CP sits on the ladder of any
 * stats row the species can wear. Null when the species has no stats.
 */
export function cpOnAnyFormLadder(
  input: Pick<CpFromHpInput, "speciesId" | "ivAtk" | "ivDef" | "ivHp">,
  cp: number,
): boolean | null {
  if (input.speciesId == null || input.ivAtk == null || input.ivDef == null || input.ivHp == null) return null;
  const rows = baseStatsCandidatesForSpecies(input.speciesId);
  if (rows.length === 0) return null;
  const iv = { atk: input.ivAtk, def: input.ivDef, sta: input.ivHp };
  return rows.some((base) => cpOnLadderOf(base, iv, cp));
}

function cpOnLadderOf(
  base: { atk: number; def: number; sta: number },
  iv: { atk: number; def: number; sta: number },
  cp: number,
): boolean {
  for (let half = 2; half <= 102; half++) {
    if (computeCp(base, iv, half / 2) === cp) return true;
  }
  return false;
}

/** "CP 1234" when exact, "CP ~1234" when the HP fits two levels. */
export function formatCpFromHp(c: CpFromHp): string {
  return `CP ${c.exact ? "" : "~"}${c.cp}`;
}
